import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { ApiService, TReconciliation } from 'legacy/lib/api/HttpService';
import SecureContent from 'legacy/app/components/security/SecureContent';
import { SECURITY_ATTRIBUTE_TYPES } from 'legacy/app/context/security';
import { useAccounts } from 'legacy/pages/accounts-payable/credit-card/reconcile/hooks';
import { CheckbookHistoryTableHeader } from 'legacy/templates/modules/accounts-payable/checkbook/CheckbookHistory/partials/CheckbookHistoryTableHeader';
import { CheckbookHistoryTableBody } from 'legacy/templates/modules/accounts-payable/checkbook/CheckbookHistory/partials/CheckbookHistoryTableBody';
import AccountsDropdown, {
	EAccountType,
	TAccountsDropdownOption,
} from 'legacy/pages/accounts-payable/credit-card/reconcile/AccountsDropdown';
import SearchButton from 'legacy/pages/accounts-payable/credit-card/reconcile/SearchButton';
import { TCheckbookReconcileSort } from 'legacy/templates/modules/accounts-payable/checkbook/CheckbookHistory/CheckbookHistoryTypes';
import { CheckbookReconcileHeader } from '../CheckbookReconcile/CheckbookReconcileHeader';
import { ECacheKeys } from 'cache/CacheKeys';

const api = new ApiService();

export const CheckbookHistory = () => {
	const [searching, setSearching] = useState(false);
	const [searches, setSearches] = useState<
		TReconciliation | Record<string, any>
	>({});
	const [sort, setSort] = useState<TCheckbookReconcileSort>([
		'statementdate',
		'desc',
	]);
	const [selectedAccount, setSelectedAccount] =
		useState<TAccountsDropdownOption | null>(null);
	const { data: accounts } = useAccounts(EAccountType.CHECKING);

	const reconciliations = useQuery({
		queryKey: [ECacheKeys.Reconciliations],
		queryFn: () => {
			return api.getReconciliations();
		},
	});

	const onAccountChange = (selected: TAccountsDropdownOption) => {
		if (selected) {
			setSelectedAccount(selected);
		}
	};

	// Set the first account.
	useEffect(() => {
		if (accounts && accounts.length > 0) {
			const [firstAccount] = accounts;
			if (firstAccount) {
				setSelectedAccount({
					label: `[${firstAccount.account}] ${firstAccount.accountn}`,
					value: firstAccount.account,
				});
			}
		}
	}, [accounts]);

	return (
		<>
			<CheckbookReconcileHeader tab={'history'} />
			<SecureContent
				attributeNo={61}
				attributeType={SECURITY_ATTRIBUTE_TYPES.DenyAccess}
			>
				<SecureContent.NoAccess>
					<div className="content-padding min-height has-action-bar mt-2">
						<SecureContent.GenericNoAccess />
					</div>
				</SecureContent.NoAccess>
				<SecureContent.HasAccess>
					{accounts && (
						<div className="content-padding min-height has-action-bar mt-2">
							<div className="a-filter border-1 border-sand bg-ivory rounderdx-4">
								<div className="d-flex flex-row justify-content-between align-items-center">
									<div className="d-flex flex-row w-50">
										<AccountsDropdown
											className="accounts-dropdown"
											onChange={onAccountChange}
											type={EAccountType.CHECKING}
											value={selectedAccount}
										/>
									</div>
									<SearchButton
										className={''}
										onClick={() => setSearching((prev) => !prev)}
										searching={searching}
									/>
								</div>
							</div>
							<div className="table-gradient sticky-container">
								<div className="table-responsive">
									<Table
										striped
										responsive
										className="a-table"
										id="cc-reconcile-history"
									>
										<CheckbookHistoryTableHeader
											searching={searching}
											searches={searches as TReconciliation}
											setSearches={
												setSearches as Dispatch<SetStateAction<TReconciliation>>
											}
											sort={sort}
											setSort={setSort}
										/>
										<CheckbookHistoryTableBody
											query={reconciliations}
											searches={searches as TReconciliation}
											selectedAccount={selectedAccount?.value ?? ''}
											sort={sort}
										/>
									</Table>
								</div>
							</div>
						</div>
					)}
				</SecureContent.HasAccess>
			</SecureContent>
		</>
	);
};

CheckbookHistory.displayName = 'CheckbookHistory';
